import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { HttpService } from '@services/http.service';
import { EndpointParams } from '@models/utils/endpoint-params';
import { GoCardlessAccount, GoCardlessMandate, GoCardlessMandateRequest, GoCardlessMandateRequestResult } from '@models/index';
import { GoCardlessMandateFlowStorage } from '@helpers/gocardless-mandate-flow.storage';

// Member-facing GoCardless direct-debit mandate API (TD-16969 FE). Talks to the TGPayments member endpoints
// added by Wave 3 (TD-16970/16971):
//   GET    payments/v1/gocardless/mandate/member               -> list the member's mandates
//   POST   payments/v1/gocardless/mandate/member/request       -> start a billing-request/redirect flow
//   POST   payments/v1/gocardless/mandate/member/{id}/complete -> finalise the mandate after the payer returns
//   PUT    payments/v1/gocardless/mandate/member/{id}/default  -> make that mandate the member's default (TD-16965)
//   POST   payments/v1/gocardless/mandate/member/{id}/cancel   -> cancel that mandate (TD-19701)
//   POST   payments/v1/gocardless/mandate/member/{id}/cancel/check -> whether that cancel is allowed (TD-19425)
// The actual direct-debit charge is NOT made here: it goes through the normal order flow (OrderService),
// which the backend collects against the member's active mandate (GoCardlessOrderPayManager).
@Injectable({
    providedIn: 'root'
})
export class GoCardlessMandateService extends HttpService {
    private readonly defaultEndpointParams: EndpointParams = { app: 'payments', version: 1 };

    private readonly mandatesSubject = new BehaviorSubject<GoCardlessMandate[]>([]);
    private readonly loadingSubject = new BehaviorSubject<boolean>(false);

    readonly mandates: Observable<GoCardlessMandate[]> = this.mandatesSubject.asObservable();
    readonly loading: Observable<boolean> = this.loadingSubject.asObservable();

    listMandates(): Observable<GoCardlessMandate[]> {
        return this.get('gocardless/mandate/member', this.defaultEndpointParams);
    }

    list(): void {
        this.loadingSubject.next(true);
        this.listMandates().subscribe({
            next: (mandates: GoCardlessMandate[]) => {
                this.mandatesSubject.next(mandates ?? []);
                this.loadingSubject.next(false);
            },
            error: () => {
                this.mandatesSubject.next([]);
                this.loadingSubject.next(false);
            }
        });
    }

    // Marks the mandate as the member's default (at most one; marking one unmarks the previous, backend-side). The
    // response is the member's already-updated bank accounts, so the list is repainted from it without a second GET.
    setDefault(idMandate: string): Observable<GoCardlessAccount[]> {
        return this.put(`gocardless/mandate/member/${idMandate}/default`, this.defaultEndpointParams, null, this.showSuccessMessage()).pipe(
            tap((accounts: GoCardlessAccount[]) => this.applyAccounts(accounts))
        );
    }

    // Asked before offering the cancel, mirroring the card and SEPA delete check: 200 when the mandate domiciles
    // nothing, and an error when it does — carrying whether there is another chargeable mandate to move those fees to.
    // The global error notification is off so the component can turn each answer into its own dialog.
    cancelCheck(idMandate: string): Observable<void> {
        return this.post(`gocardless/mandate/member/${idMandate}/cancel/check`, this.defaultEndpointParams, null, this.noShowErrorNotification);
    }

    // Cancels the mandate, at GoCardless and locally. The fees it domiciled move to another chargeable mandate when
    // there is one to move them to; the cancel/check above is what tells the member which case they are in.
    cancel(idMandate: string): Observable<GoCardlessAccount[]> {
        return this.post(`gocardless/mandate/member/${idMandate}/cancel`, this.defaultEndpointParams, null, this.showSuccessMessage('remove_successful')).pipe(
            tap((accounts: GoCardlessAccount[]) => this.applyAccounts(accounts, idMandate))
        );
    }

    requestMandate(request: GoCardlessMandateRequest = {}): Observable<GoCardlessMandateRequestResult> {
        return this.post('gocardless/mandate/member/request', this.defaultEndpointParams, request);
    }

    completeMandate(idMandate: string): Observable<GoCardlessMandate> {
        return this.post(`gocardless/mandate/member/${idMandate}/complete`, this.defaultEndpointParams);
    }

    // Repaints the list from the bank accounts both actions answer with. Those carry only SIGNED mandates, while the
    // flat list also holds the unsigned ones (abandoned signing attempts), so the known mandates are refreshed by id
    // and the rest kept: replacing the list would make those rows vanish until the next reload. `idCancelledMandate`
    // drops the one just cancelled, absent from the response precisely because it is now terminal.
    private applyAccounts(accounts: GoCardlessAccount[], idCancelledMandate?: string): void {
        const refreshedById = new Map(accounts.flatMap((account) => account.mandates).map((mandate) => [mandate.id.value, mandate]));
        const mandates = this.mandatesSubject.value.filter((mandate) => mandate.id.value !== idCancelledMandate).map((mandate) => refreshedById.get(mandate.id.value) ?? mandate);

        this.mandatesSubject.next(mandates);
    }

    // Hosted-page fallback used only when the embedded Dropin modal is unavailable (SDK blocked/failed to load):
    // persist the pending context and send the member to the GoCardless authorisation page (full-page redirect).
    // The mandate-return route finalises the mandate and navigates back to `returnUrl` afterwards.
    redirectToSign(result: GoCardlessMandateRequestResult, returnUrl: string): void {
        if (!result?.authorisationUrl) {
            return;
        }

        GoCardlessMandateFlowStorage.save({ idMandate: result.idMandate.value, returnUrl });
        window.location.assign(result.authorisationUrl);
    }
}
